import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Flame,
  Plus,
  Snowflake,
  StickyNote,
  Sun,
  Trophy,
} from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SproutCalendarIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { addDays, cn, fromDayKey, toDayKey } from "@habits/shared";
import { useAppLayoutContext } from "@/features/layout/context";
import { readInitialSearchParam } from "@/lib/initial-search";
import { type HabitWithDerived, useHabitsData } from "../hooks/use-habits-data";
import { useHabitMutations } from "../hooks/use-habit-mutations";
import { useHabitSwipeGestures } from "../hooks/use-habit-swipe-gestures";
import { useDaySwipeGestures } from "../hooks/use-day-swipe-gestures";
import { AdBreakpoint } from "@/features/growth/components/ad-breakpoint";
import { DayNoteDialog, useDayNoteDialog } from "./day-note-dialog";
import { DEFAULT_HABIT_COLOR, DEFAULT_HABIT_ICON } from "../constants";
import { isDayClosed } from "../day-closure";

const PERCENT = 100;

const UNDERLAY_MIN_OPACITY = 0.25;
const UNDERLAY_OPACITY_RANGE = 0.75;

/**
 * Colour wash + icon revealed behind a habit row as it is swiped. Tracks the
 * drag offset `tx`: right reveals the "complete" action, left the "freeze" one.
 */
function HabitSwipeUnderlay(props: { tx: () => number; commitThreshold: number }) {
  const ramp = (magnitude: number) => {
    if (magnitude <= 0) {
      return 0;
    }
    if (magnitude >= props.commitThreshold) {
      return 1;
    }
    return UNDERLAY_MIN_OPACITY + (magnitude / props.commitThreshold) * UNDERLAY_OPACITY_RANGE;
  };

  return (
    <div class="pointer-events-none absolute inset-0 z-0 flex items-center justify-between overflow-hidden rounded-xl">
      {/* Derecha: completar (verde) */}
      <div
        class="absolute inset-y-0 left-0 flex items-center pl-5 text-white"
        style={{ opacity: ramp(props.tx()), width: `${Math.max(0, props.tx())}px` }}
      >
        <div class="flex items-center rounded-full bg-emerald-500 p-1.5">
          <Check class="h-5 w-5" />
        </div>
      </div>
      {/* Izquierda: congelar/descongelar (cielo) */}
      <div
        class="absolute inset-y-0 right-0 flex items-center justify-end pr-5 text-white"
        style={{ opacity: ramp(-props.tx()), width: `${Math.max(0, -props.tx())}px` }}
      >
        <div class="flex items-center rounded-full bg-sky-500 p-1.5">
          <Snowflake class="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function TodayHabitCard(props: {
  item: HabitWithDerived;
  dayKey: string;
  onOpenNote: (item: HabitWithDerived) => void;
}) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const mutations = useHabitMutations();

  // Marking/freezing writes to local DB; waits for drizzle() to exist.
  const canWrite = () => Boolean(drizzle());

  const item = () => props.item;
  const habit = () => item().habit;
  const color = () => habit().color ?? DEFAULT_HABIT_COLOR;
  const isDone = () => item().todayStatus === "done";
  const isSkipped = () => item().todayStatus === "skipped";
  const isQuantified = () => item().goal.kind === "quantified";
  const target = () => item().goal.targetAmount;
  const amount = () => item().todayAmount;
  /** Partial progress: advancement exists but has not yet reached target. */
  const isPartial = () => isQuantified() && !isSkipped() && amount() > 0 && !isDone();

  const streakLabel = () => {
    const { current, unit } = item().streak;
    return unit === "weeks" ? t(msg`{count} wk`, { count: current }) : `${current}`;
  };

  // "Saved" feedback is now emitted by the undoable pill from mutations
  // hook itself (UNDO-TRASH-LIGHT L1): single coalesced pill with "Undo".
  const handleToggle = () => {
    if (isSkipped()) {
      return;
    }
    if (isQuantified()) {
      // +1 towards target (when reached it becomes done; see effectiveStatus).
      void mutations.incrementAmount(habit().id, props.dayKey, 1, target());
      return;
    }
    void mutations.toggleCompletion(habit().id, props.dayKey, item().todayStatus);
  };

  const handleSkip = () => {
    void mutations.setCompletion(habit().id, props.dayKey, isSkipped() ? null : "skipped");
  };

  /** Swipe-right: mark day as done (untouched if already done/frozen). */
  const handleSwipeComplete = () => {
    if (isDone() || isSkipped()) {
      return;
    }
    void mutations.setCompletion(habit().id, props.dayKey, "done");
  };

  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useHabitSwipeGestures({
    onComplete: handleSwipeComplete,
    onFreeze: handleSkip,
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <div class="relative overflow-hidden rounded-xl">
      <HabitSwipeUnderlay tx={swipe.tx} commitThreshold={swipe.COMMIT_THRESHOLD_PX} />
      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        data-testid="today-habit-card"
        data-habit-name={habit().name ?? ""}
        class={cn(
          "relative z-10 flex touch-pan-y items-center gap-3 rounded-xl border border-border/40 bg-card/60 p-3 transition-colors",
          isDone() && "bg-card/30 border-border/20",
        )}
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating()
            ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        {/* 1-tap button */}
        <button
          type="button"
          data-testid="habit-toggle"
          onClick={handleToggle}
          disabled={!canWrite()}
          aria-pressed={isDone()}
          aria-label={isDone() ? t(msg`Mark as not done`) : t(msg`Mark as done`)}
          class={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 text-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
            isDone()
              ? "scale-105 animate-habit-complete shadow-sm"
              : "hover:scale-105 active:scale-95",
            isSkipped() && "border-dashed",
          )}
          style={{
            "border-color": color(),
            "background-color": isDone() ? color() : "transparent",
          }}
        >
          <Show
            when={isDone()}
            fallback={
              <Show
                when={isSkipped()}
                fallback={
                  <Show
                    when={isPartial()}
                    fallback={<span>{habit().icon ?? DEFAULT_HABIT_ICON}</span>}
                  >
                    <span class="text-sm font-semibold" style={{ color: color() }}>
                      {amount()}
                    </span>
                  </Show>
                }
              >
                <Snowflake class="h-5 w-5" style={{ color: color() }} />
              </Show>
            }
          >
            <Check class="h-6 w-6 text-white" />
          </Show>
        </button>

        {/* Nombre + detalles */}
        <div class="min-w-0 flex-1">
          <p
            class={cn(
              "truncate text-sm font-medium",
              isDone() && "text-muted-foreground line-through decoration-1",
            )}
          >
            {habit().name}
          </p>
          <div class="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
            <span
              class={cn(
                "inline-flex items-center gap-1",
                item().streak.current > 0 && "font-semibold text-celebrate",
              )}
              data-testid="habit-streak"
            >
              <Flame
                class={cn(
                  "h-3.5 w-3.5",
                  item().streak.current > 0 ? "text-celebrate animate-streak-flicker" : "",
                )}
              />
              {streakLabel()}
            </span>
            <span class="inline-flex items-center gap-1" data-testid="habit-best-streak">
              <Trophy class="h-3.5 w-3.5" />
              {item().streak.best}
            </span>
            <Show when={item().schedule.kind === "times_per_week"}>
              <span data-testid="habit-weekly-progress">
                {t(msg`{count}/{target} this week`, {
                  count: item().weekly.count,
                  target: item().weekly.target,
                })}
              </span>
            </Show>
            <Show when={isQuantified() && !isSkipped()}>
              <span data-testid="habit-amount-progress">
                {item().goal.targetUnit
                  ? t(msg`{amount}/{target} {unit}`, {
                      amount: amount(),
                      target: target(),
                      // Message DOES have literal text ("/" and spaces); rule
                      // gets confused by fallback `?? ""`. False positive.
                      // oxlint-disable-next-line lingui/no-single-variables-to-translate
                      unit: item().goal.targetUnit ?? "",
                    })
                  : t(msg`{amount}/{target}`, { amount: amount(), target: target() })}
              </span>
            </Show>
            <Show when={isSkipped()}>
              <span class="inline-flex items-center gap-1 text-sky-400">
                <Snowflake class="h-3.5 w-3.5" />
                {t(msg`Frozen`)}
              </span>
            </Show>
          </div>
        </div>

        {/* Secondary actions */}
        <div class="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(msg`Note for today`)}
            class={cn(item().todayNote ? "text-primary" : "text-muted-foreground/60")}
            onClick={() => props.onOpenNote(item())}
          >
            <StickyNote class="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              isSkipped() ? t(msg`Remove freeze`) : t(msg`Freeze today (planned day off)`)
            }
            class={cn(isSkipped() ? "text-sky-400" : "text-muted-foreground/60")}
            onClick={handleSkip}
            disabled={!canWrite()}
            data-testid="habit-skip"
          >
            <Snowflake class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TodayView() {
  const { t, locale } = useI18n();
  const layout = useAppLayoutContext();
  const noteDialog = useDayNoteDialog();
  const search = useSearch({ from: "/_app/" });
  const navigate = useNavigate();

  // Focused day in carousel. Never advances past TODAY.
  //
  // Deep-link (#9): focused day is SHAREABLE state → lives in `?day=`. The
  // seed is read from initial URL synchronously (`readInitialSearchParam`)
  // to bypass router deferred flush on direct load from pasted link;
  // subsequent reactivity is handled by `useSearch()` (back/forward + links).
  const initialDay = readInitialSearchParam("day");
  const [viewDayKey, setViewDayKey] = createSignal(
    initialDay && initialDay <= toDayKey() ? initialDay : toDayKey(),
  );
  const data = useHabitsData({ viewDayKey });

  // Addressable day-note dialog (#15b D4): `?note=<habitId>` on focused day.
  // Note opens via URL (copyable/reloadable); close clears it.
  const openNote = (item: HabitWithDerived) => {
    void navigate({
      to: "/",
      search: (prev) => ({ ...prev, note: item.habit.id }),
      replace: true,
    });
  };
  const closeNote = () => {
    noteDialog.close();
    void navigate({ to: "/", search: (prev) => ({ ...prev, note: undefined }), replace: true });
  };

  // URL → dialog: reconstructs note target from `?note=<habitId>` + focused day
  // (direct load from link, back/forward). Permission-aware: if habit
  // is not in data, does not open (no hanging loaders or phantom states).
  createEffect(() => {
    const noteHabitId = search().note;
    if (!noteHabitId) {
      if (noteDialog.target() !== null) {
        noteDialog.close();
      }
      return;
    }
    const item = data.derived().find((i) => i.habit.id === noteHabitId);
    if (item && noteDialog.target()?.habitId !== noteHabitId) {
      noteDialog.open(item, viewDayKey());
    }
  });

  // Reflects EXTERNAL URL changes (`?day=` from calendar, back/forward)
  // into signal. Does not write URL: that is done by `focusDay` to avoid loops.
  createEffect(() => {
    const target = search().day;
    if (target && target <= toDayKey() && target !== viewDayKey()) {
      setViewDayKey(target);
    }
  });

  const isToday = () => viewDayKey() === data.todayKey();
  const canGoNext = () => viewDayKey() < data.todayKey();

  // Single point changing focused day AND reflecting it in URL (idempotent):
  // TODAY serializes as clean URL (no `?day=`); any other day remains
  // copyable/shareable. `replace:true` avoids inflating history when swiping.
  const focusDay = (next: string) => {
    if (next === viewDayKey()) {
      return;
    }
    setViewDayKey(next);
    void navigate({
      to: "/",
      search: next === data.todayKey() ? {} : { day: next },
      replace: true,
    });
  };

  const shiftDay = (delta: number) => {
    const next = addDays(viewDayKey(), delta);
    // Hard clamp at today: future cannot be viewed.
    focusDay(next > data.todayKey() ? data.todayKey() : next);
  };

  const scheduledForDay = createMemo(() =>
    data.derived().filter((item) => item.scheduledToday && item.habit.archived !== 1),
  );

  const doneCount = createMemo(
    () => scheduledForDay().filter((item) => item.todayStatus !== null).length,
  );

  const progress = createMemo(() => {
    const total = scheduledForDay().length;
    return total === 0 ? 0 : Math.round((doneCount() / total) * PERCENT);
  });

  /**
   * Closed value unit: **all** scheduled habits for TODAY marked.
   *
   * It is the ONLY breathing point in the product and therefore the only declared
   * breakpoint (`day-done`). Marking ONE habit is not: it would be an ad in the middle
   * of a task, exactly what strategy §3 prohibits — and in a streak tracker, furthermore,
   * it would break the momentum the app exists to build. PAST days
   * do not count either: reviewing history is not closing anything.
   */
  const dayIsDone = createMemo(() =>
    isDayClosed({
      isToday: isToday(),
      scheduled: scheduledForDay().length,
      done: doneCount(),
    }),
  );

  const dayLabel = () =>
    fromDayKey(viewDayKey()).toLocaleDateString(locale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
    });

  let surfaceRef: HTMLDivElement | undefined;
  const daySwipe = useDaySwipeGestures({
    onShiftDay: shiftDay,
    canGoNext,
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <div
      class="flex flex-1 flex-col overflow-y-auto px-4 pb-8 pt-2 md:px-8"
      data-testid="today-view"
    >
      {/* Header */}
      <div class="mb-4 flex items-end justify-between gap-3">
        <div class="min-w-0">
          <div class="flex items-center gap-2 text-primary">
            <Sun class="h-5 w-5" />
            <h1 class="text-xl font-semibold tracking-tight">
              {isToday() ? t(msg`Today`) : t(msg`History`)}
            </h1>
          </div>
          <div class="mt-0.5 flex items-center gap-1">
            <button
              type="button"
              onClick={() => shiftDay(-1)}
              aria-label={t(msg`Previous day`)}
              class="rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              data-testid="today-prev-day"
            >
              <ChevronLeft class="h-4 w-4" />
            </button>
            <p
              class="truncate text-sm capitalize text-muted-foreground"
              data-testid="today-day-label"
            >
              {dayLabel()}
            </p>
            <button
              type="button"
              onClick={() => shiftDay(1)}
              disabled={!canGoNext()}
              aria-label={t(msg`Next day`)}
              class="rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:opacity-30 disabled:hover:text-muted-foreground"
              data-testid="today-next-day"
            >
              <ChevronRight class="h-4 w-4" />
            </button>
          </div>
        </div>
        <Button onClick={() => layout.openNewHabitDialog()} data-testid="new-habit-button">
          <Plus class="mr-1 h-4 w-4" />
          {t(msg`New habit`)}
        </Button>
      </div>

      {/* Day carousel: owns horizontal axis (data-swipe-own) so that
          page-swipe between tabs yields the gesture. `touch-action: pan-y`
          preserves native vertical scroll. */}
      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="flex flex-1 flex-col"
        style={{ "touch-action": "pan-y" }}
        data-swipe-own="true"
        {...daySwipe.handlers}
      >
        <div
          class="flex flex-1 flex-col will-change-transform"
          style={{
            transform: `translate3d(${daySwipe.dragX()}px, 0, 0)`,
            transition: daySwipe.transitioning() ? "transform 240ms ease-out" : "none",
          }}
        >
          {/* Day progress */}
          <Show when={scheduledForDay().length > 0}>
            <div class="mb-5">
              <div class="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="today-progress">
                  {t(msg`{done} of {total} done`, {
                    done: doneCount(),
                    total: scheduledForDay().length,
                  })}
                </span>
                <span>{progress()}%</span>
              </div>
              <div class="h-2 overflow-hidden rounded-full bg-muted/40">
                <div
                  class={cn(
                    "h-full rounded-full transition-all duration-500 ease-out",
                    progress() >= PERCENT
                      ? "bg-gradient-to-r from-accent to-celebrate"
                      : "bg-gradient-to-r from-accent/80 to-accent",
                  )}
                  style={{ width: `${progress()}%` }}
                />
              </div>
              <Show when={progress() >= PERCENT}>
                <p class="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-celebrate animate-completed-reveal">
                  <Trophy class="h-3.5 w-3.5" />
                  {isToday()
                    ? t(msg`All done for today. Keep the streak alive!`)
                    : t(msg`All done this day. Nice work!`)}
                </p>
              </Show>
            </div>
          </Show>

          {/*
            Breathing point (growth kit): day became COMPLETE. With ads
            OFF (default) renders nothing and loads no SDK; gate adds
            capping (1/session, never on first session of user lifecycle).
          */}
          <AdBreakpoint placement="day-done" trigger={dayIsDone()} />

          {/* Day list */}
          <Show
            when={scheduledForDay().length > 0 || data.isLoading()}
            fallback={
              <EmptyState
                icon={Sun}
                illustration={SproutCalendarIllustration}
                title={isToday() ? t(msg`Today is a fresh start`) : t(msg`Nothing scheduled`)}
                description={
                  isToday()
                    ? t(msg`Create your first habit and start your streak today.`)
                    : t(msg`No habits were scheduled for this day.`)
                }
                onIconActivate={isToday() ? () => layout.openNewHabitDialog() : undefined}
                iconActionLabel={t(msg`Create your first habit`)}
                action={
                  <Show when={isToday()}>
                    <Button
                      onClick={() => layout.openNewHabitDialog()}
                      data-testid="empty-new-habit-button"
                    >
                      <Plus class="mr-1 h-4 w-4" />
                      {t(msg`Create your first habit`)}
                    </Button>
                  </Show>
                }
              />
            }
          >
            <div class="flex flex-col gap-2" data-testid="today-habit-list">
              <For each={scheduledForDay()}>
                {(item, index) => (
                  <div class="habit-row-enter" style={{ "--row": index() }}>
                    <TodayHabitCard item={item} dayKey={viewDayKey()} onOpenNote={openNote} />
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>

      <DayNoteDialog controller={noteDialog} onClose={closeNote} />
    </div>
  );
}
